import { ECreditApplicationStatus, EDecision } from '@if/core/types'
import { CheckboxProps, CircularProgress, Typography } from '@mui/material'
import AsyncActionButton from '@src/components/AsyncActionButton'
import StepStatusSection from '@src/containers/App/components/StepStatusSection'
import GetCreditContentConsentText from '@src/containers/CreditApplicationPage/components/creditApplicationForm-selectors'
import { useQualify } from '@src/data/api/credit-api/credit-api'
import { anyDocsAwaitingApproval } from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CreditConsentDialog from '../../../../components/CreditConsentDialog'
import { canPollCreditApp, isServerAnalysingCredit } from '../../credit-hooks'
import { ECreditApplicationStatusMessage, GetDefaultIconAndMessageFromDecision } from '../../viewCreditApp-selectors'
import ApplicationError from '../applicationError'
import CreditApproved from './CreditApproved'
import CreditDeclined from './CreditDeclined'
import CreditDocumentsRequired from './CreditDocumentsRequired'
import CreditManualRevision from './CreditManualRevision'
import PrequalNotCompleted from './PrequalNotCompleted'
import RequiresIntervention from './RequiresIntervention'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
  canEdit: boolean
}

const CreditTile = ({ creditApp, merchant, canEdit }: Props) => {
  const { t } = useTranslation()
  const [openConsentDialog, setOpenConsentDialog] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)

  const [qualify, isQualifying, isError, isSuccess] = useQualify()
  const isAnalysing = isServerAnalysingCredit(creditApp) && canPollCreditApp(creditApp)
  const docsAwaitingApproval = anyDocsAwaitingApproval(creditApp.requiredDocuments)

  const handleCheckboxChange: CheckboxProps['onChange'] = (event) => {
    setConsentChecked(event.target.checked)
  }

  const handleConfirm = async () => {
    if (consentChecked) {
      setOpenConsentDialog(false)
      await qualify({
        creditApplicationId: creditApp.id!,
        financingProgramId: creditApp.financingProgramId,
        prequalPromoCode: creditApp.prequalPromoCode ?? undefined,
      })
    }
  }

  if (creditApp.prequalificationDecision && creditApp.prequalificationDecision.decision !== EDecision.Approved) {
    return (
      <StepStatusSection title={t('viewCreditApplication.creditSectionTitle')}>
        <PrequalNotCompleted />
      </StepStatusSection>
    )
  }
  const icon = GetDefaultIconAndMessageFromDecision(
    creditApp.finalCreditDecision.decision,
    false,
    creditApp,
    isAnalysing,
    docsAwaitingApproval,
  )

  if (
    creditApp.worksheet === null &&
    creditApp.consentHardHit === true &&
    !canEdit &&
    FinancingProgramConfigs[creditApp.financingProgramId].worksheetIsNeededForCredit
  ) {
    return (
      <StepStatusSection
        title={t('viewCreditApplication.creditSectionTitle')}
        iconMessage={icon.iconMessage ? t(`creditApplicationStatus.${icon.iconMessage}`) : null}
        iconType={icon.iconType}
      >
        {creditApp.finalCreditDecision?.decision === EDecision.Refused ? (
          <>
            <Typography variant="body1">{t('viewCreditApplication.clientDeclined')}</Typography>
            {!creditApp.coApplicant ? (
              <Typography variant="body1">{t('viewCreditApplication.applicantAddCoappInvite')}</Typography>
            ) : (
              <Typography variant="body1">{t('viewCreditApplication.applicantUpdateCoappInvite')}</Typography>
            )}
          </>
        ) : (
          <Typography>{t('viewCreditApplication.awaitingClientApproval')}</Typography>
        )}
      </StepStatusSection>
    )
  }
  return (
    <StepStatusSection
      title={t('viewCreditApplication.creditSectionTitle')}
      iconMessage={icon.iconMessage ? t(`creditApplicationStatus.${icon.iconMessage}`) : null}
      iconType={icon.iconType}
    >
      {creditApp.consentHardHit === false && creditApp.prequalificationDecision === null ? (
        <Typography>{t('viewCreditApplication.notReady')}</Typography>
      ) : (
        <>
          {isAnalysing && creditApp.status === ECreditApplicationStatus.Active && (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
            </div>
          )}
          {!creditApp.consentHardHit && creditApp.status === ECreditApplicationStatus.Active && !canEdit && (
            <Typography>{t('viewCreditApplication.awaitingClientConsent')}</Typography>
          )}

          {!creditApp.consentHardHit && creditApp.status === ECreditApplicationStatus.Active && canEdit && (
            <>
              <AsyncActionButton
                onClick={() => setOpenConsentDialog(true)}
                isPending={isQualifying}
                isError={isError}
                isSuccess={isSuccess}
                variant="contained"
              >
                {t('viewCreditApplication.qualify')}
              </AsyncActionButton>
              <CreditConsentDialog
                openConsentDialog={openConsentDialog}
                setOpenConsentDialog={setOpenConsentDialog}
                consentChecked={consentChecked}
                handleCheckboxChange={handleCheckboxChange}
                handleConfirm={handleConfirm}
                dialogContent={<GetCreditContentConsentText financingProgram={creditApp.financingProgramId} />}
                buttonText={t('submission.submit')}
              />
            </>
          )}
          {icon.iconMessage === ECreditApplicationStatusMessage.Error && <ApplicationError />}
          {icon.iconMessage === ECreditApplicationStatusMessage.Cancelled &&
            creditApp.finalCreditDecision.decision !== EDecision.Approved && (
              <Typography variant="body1">{t('viewCreditApplication.creditAppCancelled')}</Typography>
            )}

          {creditApp.finalCreditDecision.decision !== EDecision.Pending && (
            <>
              {FinancingProgramConfigs[creditApp.financingProgramId].requiresCreditStepIntervention && (
                <RequiresIntervention creditApp={creditApp} />
              )}

              {creditApp.finalCreditDecision.decision === EDecision.Approved && (
                <CreditApproved creditApp={creditApp} merchant={merchant} />
              )}
              {icon.iconMessage === ECreditApplicationStatusMessage.Declined && (
                <CreditDeclined creditApp={creditApp} canEdit={canEdit} />
              )}
              {icon.iconMessage === ECreditApplicationStatusMessage.DocumentsRequired && (
                <CreditDocumentsRequired creditapp={creditApp} canEdit={canEdit} />
              )}
              {icon.iconMessage === ECreditApplicationStatusMessage.ManualRevision && (
                <CreditManualRevision creditApp={creditApp} canEdit={canEdit} />
              )}
              {icon.iconMessage === ECreditApplicationStatusMessage.AwaitingApproval && (
                <Typography>{t('viewCreditApplication.clientManualRevision')}</Typography>
              )}
            </>
          )}
        </>
      )}
    </StepStatusSection>
  )
}

export default CreditTile
